// Timezone utilities for editing wall-clock times in the *offering's*
// timezone rather than the browser's.
//
// The problem: <input type="date|time|datetime-local"> works in plain wall
// clock, but the editor previously converted those values through the
// browser's local zone (new Date().setHours etc). An organiser in Abidjan
// (GMT) editing an offering whose times render in Africa/Johannesburg
// (GMT+2) would see every saved time shift by two hours. These helpers do
// the conversion through an explicit IANA timezone instead, with no library
// dependency — just Intl.

export const DEFAULT_TIMEZONE = "Africa/Johannesburg";

/** Curated picker options: Paystack markets first, then common hubs. The
 *  picker merges in the offering's current zone and the browser zone if
 *  they're not listed, so no stored value is ever unrepresentable. */
export const TIMEZONE_OPTIONS: string[] = [
  // Paystack markets
  "Africa/Johannesburg", // South Africa
  "Africa/Abidjan", // Côte d'Ivoire (GMT)
  "Africa/Lagos", // Nigeria
  "Africa/Accra", // Ghana
  "Africa/Nairobi", // Kenya
  // Wider Africa
  "Africa/Cairo",
  "Africa/Casablanca",
  "Africa/Windhoek",
  "Africa/Harare",
  // Common international hubs
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Lisbon",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

/** "Africa/Abidjan" → "Abidjan"; "America/New_York" → "New York" */
export function tzCityLabel(timeZone: string): string {
  const city = timeZone.split("/").pop() ?? timeZone;
  return city.replace(/_/g, " ");
}

/** Current UTC offset of a zone as "GMT+02:00" / "GMT" → "GMT+00:00". */
export function tzOffsetLabel(timeZone: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      timeZoneName: "longOffset",
    }).formatToParts(at);
    const name = parts.find((p) => p.type === "timeZoneName")?.value ?? "GMT";
    return name === "GMT" ? "GMT+00:00" : name;
  } catch {
    return "GMT+00:00";
  }
}

/** "Abidjan (GMT+00:00)" — the picker's display label. */
export function tzPickerLabel(timeZone: string, at: Date = new Date()): string {
  return `${tzCityLabel(timeZone)} (${tzOffsetLabel(timeZone, at)})`;
}

/** Compact offset for public pages: "GMT+2", "GMT", "GMT+5:30". */
export function tzShortOffsetLabel(
  timeZone: string,
  at: Date = new Date()
): string {
  const long = tzOffsetLabel(timeZone, at); // GMT+02:00
  const m = long.match(/^GMT([+-])(\d{2}):(\d{2})$/);
  if (!m) return "GMT";
  const [, sign, hh, mm] = m;
  const h = Number(hh);
  if (h === 0 && mm === "00") return "GMT";
  return mm === "00" ? `GMT${sign}${h}` : `GMT${sign}${h}:${mm}`;
}

type Moment = string | Date | null | undefined;
/** A continuous stretch of time — an event that runs from one to the other. */
type MomentSpan = readonly [Moment, Moment];

function toDateOrNull(value: Moment): Date | null {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

// Clock changes come at most twice a year, so a sample every four weeks
// cannot step over one unseen. The step widens for absurdly long ranges so
// the sample count stays bounded.
const SAMPLE_STEP_MS = 28 * 24 * 60 * 60 * 1000;
const MAX_SAMPLES = 500;

/** Every instant worth asking about within a range, endpoints included. */
function samplesAcross(from: Date, to: Date): Date[] {
  const [start, end] =
    from.getTime() <= to.getTime() ? [from, to] : [to, from];
  const span = end.getTime() - start.getTime();
  const step = Math.max(SAMPLE_STEP_MS, Math.ceil(span / MAX_SAMPLES));
  const out: Date[] = [start];
  for (let t = start.getTime() + step; t < end.getTime(); t += step) {
    out.push(new Date(t));
  }
  out.push(end);
  return out;
}

/**
 * The "times are in ..." hint for a public page.
 *
 * An offset belongs to an instant, not to a zone: a six-week course or a
 * stay that straddles a clock change has more than one. So the offset is
 * only quoted when every moment on the page shares it, and otherwise the
 * city stands alone rather than stating an offset that is wrong for half
 * the schedule.
 *
 *   one October evening in Johannesburg   → "GMT+2, Johannesburg time"
 *   a London course either side of March  → "London time"
 *
 * Pass a `[start, end]` tuple for anything continuous. A range is sampled
 * through its middle, not just at its ends: a London event running from
 * January to December is GMT at both ends and BST for the summer.
 */
export function tzHintLabel(
  timeZone: string,
  moments: (Moment | MomentSpan)[] = []
): string {
  const city = tzCityLabel(timeZone);

  const instants: Date[] = [];
  for (const entry of moments) {
    if (Array.isArray(entry)) {
      const from = toDateOrNull(entry[0]);
      const to = toDateOrNull(entry[1]);
      if (from && to) instants.push(...samplesAcross(from, to));
      else if (from ?? to) instants.push((from ?? to) as Date);
      continue;
    }
    const at = toDateOrNull(entry as Moment);
    if (at) instants.push(at);
  }

  const offsets = new Set(
    (instants.length > 0 ? instants : [new Date()]).map((d) =>
      tzShortOffsetLabel(timeZone, d)
    )
  );
  if (offsets.size !== 1) return `${city} time`;
  return `${[...offsets][0]}, ${city} time`;
}

/** The visitor's IANA zone, or the default when detection fails (SSR). */
export function detectTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

/** Options for a picker: curated list + always the given extras (current
 *  value, browser zone), deduped, in curated order with extras appended. */
export function timeZoneOptionsWith(...extras: (string | null | undefined)[]) {
  const all = [...TIMEZONE_OPTIONS];
  for (const tz of extras) {
    if (tz && !all.includes(tz) && isValidTimeZone(tz)) all.push(tz);
  }
  return all;
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone });
    return true;
  } catch {
    return false;
  }
}

// ─── Wall-clock ↔ instant conversion ────────────────────────────────────────

type WallParts = {
  year: number;
  month: number; // 1–12
  day: number;
  hour: number;
  minute: number;
  second: number;
};

function wallPartsOf(date: Date, timeZone: string): WallParts {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hourCycle: "h23",
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
  };
}

/** Zone offset (ms east of UTC) at a given instant. */
function tzOffsetMs(ts: number, timeZone: string): number {
  const w = wallPartsOf(new Date(ts), timeZone);
  const asUTC = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  return asUTC - ts;
}

/** A wall-clock time in `timeZone` → the UTC instant it names. Two-pass
 *  offset lookup converges across DST transitions. */
function wallToInstant(p: WallParts, timeZone: string): Date {
  const wallUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  let ts = wallUTC - tzOffsetMs(wallUTC, timeZone);
  ts = wallUTC - tzOffsetMs(ts, timeZone);
  return new Date(ts);
}

const pad = (n: number) => String(n).padStart(2, "0");

/** ISO instant → "yyyy-MM-dd" as seen in `timeZone` (for <input type=date>). */
export function toDateInputValue(
  iso: string | Date,
  timeZone?: string | null
): string {
  const w = wallPartsOf(
    iso instanceof Date ? iso : new Date(iso),
    timeZone || DEFAULT_TIMEZONE
  );
  return `${w.year}-${pad(w.month)}-${pad(w.day)}`;
}

/** ISO instant → "HH:mm" as seen in `timeZone` (for <input type=time>). */
export function toTimeInputValue(
  iso: string | Date,
  timeZone?: string | null
): string {
  const w = wallPartsOf(
    iso instanceof Date ? iso : new Date(iso),
    timeZone || DEFAULT_TIMEZONE
  );
  return `${pad(w.hour)}:${pad(w.minute)}`;
}

/** ISO instant → "yyyy-MM-ddTHH:mm" (for <input type=datetime-local>). */
export function toDateTimeInputValue(
  iso: string | null,
  timeZone?: string | null
): string {
  if (!iso) return "";
  return `${toDateInputValue(iso, timeZone)}T${toTimeInputValue(iso, timeZone)}`;
}

/** "yyyy-MM-ddTHH:mm" typed in `timeZone` → ISO instant. */
export function fromDateTimeInputValue(
  local: string,
  timeZone?: string | null
): string | null {
  if (!local) return null;
  const [datePart, timePart = "00:00"] = local.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);
  return wallToInstant(
    { year: y, month: m, day: d, hour: hh, minute: mm, second: 0 },
    timeZone || DEFAULT_TIMEZONE
  ).toISOString();
}

/** Combine date "yyyy-MM-dd" + time "HH:mm" (both wall clock in `timeZone`)
 *  into an ISO instant. */
export function dateTimeToISO(
  date: string,
  time: string,
  timeZone?: string | null
): string {
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  return wallToInstant(
    { year: y, month: m, day: d, hour: hh, minute: mm, second: 0 },
    timeZone || DEFAULT_TIMEZONE
  ).toISOString();
}

/** Replace the calendar date of `iso` (or seed 09:00 when null) with
 *  `datePart`, keeping the wall-clock time in `timeZone`. */
export function combineDateInTZ(
  iso: string | null,
  datePart: string,
  timeZone?: string | null
): string | null {
  if (!datePart) return null;
  const time = iso ? toTimeInputValue(iso, timeZone) : "09:00";
  return dateTimeToISO(datePart, time, timeZone);
}

/** Replace the wall-clock time of `iso` (or seed today when null) with
 *  `timePart`, keeping the calendar date in `timeZone`. */
export function combineTimeInTZ(
  iso: string | null,
  timePart: string,
  timeZone?: string | null
): string | null {
  if (!timePart) return null;
  const date = toDateInputValue(iso ? iso : new Date(), timeZone);
  return dateTimeToISO(date, timePart, timeZone);
}

/** Re-anchor an instant so its wall-clock reading stays the same in a new
 *  zone: "14:00 in Johannesburg" → "14:00 in Abidjan" (a different instant).
 *  This is what organisers mean when they change an event's timezone. */
export function shiftInstantKeepWallClock(
  iso: string | null,
  fromTz: string | null,
  toTz: string | null
): string | null {
  if (!iso) return null;
  const w = wallPartsOf(new Date(iso), fromTz || DEFAULT_TIMEZONE);
  return wallToInstant(w, toTz || DEFAULT_TIMEZONE).toISOString();
}

/** Best-guess Paystack charge currency for an organiser's timezone. Used
 *  only to seed brand-new drafts — always changeable in the editor. */
export function defaultCurrencyForTimeZone(
  timeZone: string
): "ZAR" | "NGN" | "GHS" | "USD" | "KES" | "XOF" {
  const map: Record<string, "ZAR" | "NGN" | "GHS" | "KES" | "XOF"> = {
    "Africa/Johannesburg": "ZAR",
    "Africa/Lagos": "NGN",
    "Africa/Accra": "GHS",
    "Africa/Nairobi": "KES",
    // WAEMU (XOF) capitals — browsers report whichever the OS picked.
    "Africa/Abidjan": "XOF",
    "Africa/Dakar": "XOF",
    "Africa/Bamako": "XOF",
    "Africa/Ouagadougou": "XOF",
    "Africa/Lome": "XOF",
    "Africa/Niamey": "XOF",
    "Africa/Porto-Novo": "XOF",
    "Africa/Bissau": "XOF",
  };
  return map[timeZone] ?? "ZAR";
}

/** Calendar-day arithmetic on a "yyyy-MM-dd" string (no timezone involved). */
export function addDaysToDateString(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + days));
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
}
